<template>
  <jczh-table-content
    ref="multipleTable"
    board
    :data="listData"
    :option="tableOption"
    pagination
    :page.sync="page"
    :limit.sync="size"
    :total="total"
    @sort-change="sortChange"
  >
    <template v-slot:orderStatus="{ row }">
      <status-tag
        :type="negotiationV2FlowStatusDict.translate(row.orderStatus).type"
        :label="
          negotiationV2FlowStatusDict.translate(row.orderStatus).commonLabel
        "
        size="small"
      />
    </template>
    <template v-slot:last>
      <el-table-column label="操作" align="center" width="160" fixed="right">
        <template slot-scope="{ row }">
          <el-button
            v-if="hasPermission(row.orderStatus, 'edit')"
            type="text"
            @click="jumpToEditPage(row)"
          >
            编辑
          </el-button>
          <el-button
            v-if="hasPermission(row.orderStatus, 'del')"
            type="text"
            class="text-danger"
            @click="deleteItem(row)"
          >
            删除
          </el-button>
          <el-button
            v-if="hasPermission(row.orderStatus, 'copy')"
            type="text"
            @click="copyItem(row)"
          >
            复制谈判
          </el-button>
          <el-button
            v-if="hasPermission(row.orderStatus, 'look')"
            type="text"
            @click="jumpDetail(row)"
          >
            查看详情
          </el-button>
        </template>
      </el-table-column>
    </template>
  </jczh-table-content>
</template>

<script>
import StatusTag from '@/components/common/StatusTag.vue'

import { projectTypeDict, whetherDict } from '@/dictionary/project/common'
import {
  negotiationV2FlowStatusDict,
  negotiateTypeDict,
  reviewNegotiationStatusDict
} from '@/dictionary/project/negotiation-v2'

import {
  getNegotiationV2ListApi,
  deleteNegotiationV2OrderApi
} from '@/api/procure/negotiation-v2'

import { strToThousands } from '@/utils/utils'
import { workbenchServiceDict } from '@/dictionary/system'
import { mapState } from 'vuex'

export default {
  components: { StatusTag },
  props: {
    filterForm: {
      type: Object,
      default: () => {}
    },

    fetchApi: {
      type: Function,
      default: getNegotiationV2ListApi
    },

    // 是否是委托项目, 委托项目列表新增三项：项目名称、委托方、负责人
    isEntrust: {
      type: Boolean,
      default: false
    },

    pageNum: {
      type: [String, Number],
      default: 1
    },

    pageSize: {
      type: [String, Number],
      default: 10
    }
  },

  data() {
    return {
      negotiationV2FlowStatusDict,

      total: 0,
      listData: []
    }
  },

  computed: {
    ...mapState('router', ['currentWorkbench']),

    page: {
      get() {
        return this.pageNum
      },
      set(val) {
        this.$emit('update:pageNum', val)
      }
    },

    size: {
      get() {
        return this.pageSize
      },
      set(val) {
        this.$emit('update:pageSize', val)
      }
    },

    // 获取当前路由参数，跳转路由前缀，代理机构加上Agency
    entrustRouter() {
      if (this.currentWorkbench === workbenchServiceDict.AGENCY.value) {
        return 'Agency'
      }
      return ''
    },

    hasPermission() {
      return (status, buttonType) => {
        if (!negotiationV2FlowStatusDict.translate(status)) return false

        const operateList = negotiationV2FlowStatusDict.translate(status)
          .permission

        return operateList.includes(buttonType)
      }
    },

    tableOption() {
      const common = [
        { label: '谈判名称', prop: 'orderName' },
        { label: '谈判编号', prop: 'customOrderCode' },
        {
          label: '项目类型',
          prop: 'projectType',
          filter: value => projectTypeDict.translate(value).label || '-'
        },
        {
          label: '谈判预算（万元）',
          prop: 'budgetAmountWy',
          filter: value => (value ? `${strToThousands(value)}` : '-'),
          columnAttrs: {
            align: 'right',
            headerAlign: 'center'
          }
        },
        {
          label: '谈判状态',
          prop: 'orderStatus',
          slotName: 'orderStatus'
        },
        {
          label: '发布日期',
          prop: 'releaseTime',
          minWidth: 120,
          columnAttrs: {
            prop: 'releaseTime',
            sortable: 'custom'
          },
          isNormDay: true
        },
        {
          label: '响应文件截止时间',
          prop: 'negotiateTime',
          minWidth: 140,
          columnAttrs: {
            prop: 'negotiateTime',
            sortable: 'custom'
          },
          isNormMinute: true
        },
        {
          label: '线上评审',
          prop: 'isOnlineReview',
          minWidth: 80,
          filter: value => whetherDict.translate(value).label || '-'
        },
      ]
      const entrustItem = [
        {
          label: '项目名称',
          prop: 'entrustProjectName',
          minWidth: 150
        },
        {
          label: '委托方',
          prop: 'entrustPurchaseCompanyName',
          minWidth: 150
        },
        {
          label: '负责人',
          prop: 'entrustUserName',
          minWidth: 150
        }
      ]
      return this.isEntrust ? [...entrustItem, ...common] : common
    }
  },

  activated() {
    this.getListData()
  },

  methods: {
    /**
     * 获取列表详情
     */
    async getListData() {
      try {
        const data = Object.assign(
          {},
          {
            negotiateType: negotiateTypeDict.COMMON.value,
            page: this.page,
            size: this.size
          },
          this.filterForm
        )
        const requestParams = {}
        for (const key in data) {
          if (data[key] || data[key] == 0) {
            requestParams[key] = data[key]
          }
        }
        const _data = await this.fetchApi(requestParams)
        this.listData = _data?.data?.records || []
        this.total = _data?.data?.total || 0
      } catch (error) {
        this.$message.error(error?.message)
      }
    },

    /**
     * 复制
     */
    async copyItem(row) {
      await this.$confirm(`复制 “${row.orderName}” 的谈判信息`, '复制项目', {
        confirmButtonText: '确定复制',
        cancelButtonText: '取消',
        type: 'warning',
        center: true
      })
      this.$router.push({
        name: this.entrustRouter + 'NegotiationForm',
        params: {
          orderId: row.orderId,
          projectId: row.projectId,
          isCopy: 'yes'
        }
      })
    },

    /**
     * 跳转到详情
     */
    jumpDetail(row) {
      let routerName = ''
      if (row.orderFlowStatus) {
        routerName = negotiationV2FlowStatusDict.translate(row.orderFlowStatus)
          .routerCommonName
      } else {
        routerName = negotiationV2FlowStatusDict.translate(row.orderStatus)
          .routerCommonName
      }

      if (row.orderStatus === negotiationV2FlowStatusDict.RESPONSE_END.value) {
        const routerObj = {
          [reviewNegotiationStatusDict.INIT.value]: 'NegotiationResponse',
          [reviewNegotiationStatusDict.DOING.value]: 'NegotiationReview',
          [reviewNegotiationStatusDict.FINISH.value]: row.orderFlowStatus ? routerName : 'NegotiationCandidate'
        }
        this.$router.push({
          name: this.entrustRouter + routerObj[row.reviewStatus],
          params: {
            orderId: row.orderId
          }
        })
        return
      }
      this.$router.push({
        name: this.entrustRouter + routerName,
        params: {
          orderId: row.orderId
        }
      })
    },

    /**
     * 删除
     */
    async deleteItem(row) {
      const inviteBidName = row.orderName
      const _inviteBidName = inviteBidName ? `“${inviteBidName}”的` : ''
      await this.$confirm(`删除${_inviteBidName}谈判`, '删除谈判', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      })
      try {
        await deleteNegotiationV2OrderApi(row.orderId)
        this.$message.success('删除成功')
        await this.getListData()
      } catch (error) {
        this.$message.error(error.message)
      }
    },

    /**
     * 跳转到编辑页面
     */
    jumpToEditPage(row) {
      this.$router.push({
        name: this.entrustRouter + 'NegotiationForm',
        params: { orderId: row.orderId, projectId: row.projectId }
      })
    },

    /**
     * 按顺序排序
     */
    sortChange(column, prop, order) {
      this.filterForm.sortBy = column.prop
        .replace(/([A-Z])/g, '_$1')
        .toLowerCase()
      if (column.order == 'ascending') {
        this.filterForm.sortType = 'asc'
      } else if (column.order == 'descending') {
        this.filterForm.sortType = 'desc'
      } else {
        this.filterForm.sortType = undefined
      }
      this.getListData()
    },

    /**
     * 清除排序
     */
    clearSort() {
      this.$refs.multipleTable?.$refs.table.clearSort()
    }
  }
}
</script>
